import { assign, DoneActorEvent, ErrorActorEvent, fromPromise, setup } from 'xstate';
import { sendCustomError, showAxiosErrorAction } from '~/features/actions';
import {
  acceptConflictCode,
  feeRefusalCode,
  isPayerFeeAuthorizationRequired,
  isUncertainListingActionError,
  sendListingActionError,
  type FeeRefusalCode,
} from '~/features/market/listing-search/errors';
import { AppActorSystem } from '~/features/xstateSystem';
import { MsaasNegotiation } from '~/types/hlx/msaasNegotiation';
import { MARKET_READ_TIMEOUT_MS } from '../requestPolicy';
import { API_CALLS } from './api';
import {
  Context,
  DoneGetEvent,
  DraftSubmitResult,
  Events,
  NegotiationFormValues,
  OnOpenDraftEvent,
  Scope,
} from './types';
import {
  isNegotiationReadOnlyForViewer,
  isSameNegotiationDraftSource,
  NegotiationDraftApiResponse,
  transformDraftApiToDraft,
} from './utils';

const isDoneGetEvent = (event: Scope['event']): event is DoneGetEvent => 'output' in event;

const getNegotiationsNewestFirst = (event: DoneGetEvent): NegotiationFormValues[] =>
  [...event.output].sort((left, right) => {
    return (right.version ?? 0) - (left.version ?? 0);
  });

// [MCV-1646] Shape of the axios error the server returns when a chain has
// already been started (by the other party, or by a same-caller retry that
// lost a race). Read defensively -- these come off an unknown `event.error`.
type AlreadyStartedError = {
  response?: {
    status?: number;
    data?: { detail?: unknown; extra?: { code?: string; jobResponseId?: number } };
  };
};

const asAlreadyStartedError = (error: unknown): AlreadyStartedError | undefined =>
  error != null && typeof error === 'object' && 'response' in error
    ? (error as AlreadyStartedError)
    : undefined;

const isNegotiationAlreadyStartedError = (error: unknown): boolean =>
  asAlreadyStartedError(error)?.response?.data?.extra?.code === 'NEGOTIATION_ALREADY_STARTED';

const getAlreadyStartedResponseId = (error: unknown): number | undefined =>
  asAlreadyStartedError(error)?.response?.data?.extra?.jobResponseId;

const getErrorStatus = (error: unknown): number | undefined =>
  asAlreadyStartedError(error)?.response?.status;

const getErrorMessage = (event: ErrorActorEvent): string =>
  event.error instanceof Error ? event.error.message : 'Something went wrong. Please try again.';

const isReopeningUncertainSubmission = ({ context, event }: Scope): boolean => {
  if (event.type !== 'ON_LOAD' && event.type !== 'ON_OPEN_DRAFT') return false;
  const source =
    event.type === 'ON_LOAD'
      ? { responseId: event.payload.responseId }
      : { listingType: event.payload.listingType, listingId: event.payload.listingId };
  return (
    !!context.submitError?.uncertain &&
    !!context.submittedDraft &&
    context.employerUser?.id === event.payload.employerUser?.id &&
    context.employerUser?.employerId === event.payload.employerUser?.employerId &&
    (isSameNegotiationDraftSource(context.draftSource, source) ||
      (event.type === 'ON_LOAD' &&
        context.draftSource !== undefined &&
        'listingId' in context.draftSource &&
        event.payload.responseId === context.responseId &&
        (event.payload.responseType ?? 'msaas') === context.responseType &&
        event.payload.viewerRole === context.viewerRole))
  );
};

export const negotiationMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
    output: {} as { data: MsaasNegotiation[] },
  },
  actors: {
    getNegotiations: fromPromise(API_CALLS.getByResponseId),
    rejectNegotiation: fromPromise(API_CALLS.rejectNegotiation),
    withdrawNegotiation: fromPromise(API_CALLS.withdrawNegotiation),
    acceptNegotiation: fromPromise(API_CALLS.acceptNegotiation),
    counterNegotiation: fromPromise(API_CALLS.counterNegotiation),
    calculate: fromPromise(API_CALLS.calculate),
    getDraft: fromPromise(API_CALLS.getDraft),
    submitDraft: fromPromise(API_CALLS.submitDraft),
  },
  guards: { isReopeningUncertainSubmission },
  actions: {
    clearNegotiationScope: assign({
      negotiations: [],
      negotiation: undefined,
      responseId: undefined,
      draft: undefined,
      draftSource: undefined,
      draftProviderIds: undefined,
      submittedDraft: undefined,
      submitError: undefined,
      submitResult: undefined,
      submitResultSource: undefined,
      counterResult: undefined,
      acceptRefusal: undefined,
      startDraftIfEmpty: false,
      loadFailed: false,
      offerModal: { open: false },
    }),
    setDeclinedResult: assign({
      counterResult: ({ context }: Scope) => ({
        responseId: context.responseId ?? context.negotiation?.jobResponse?.id,
        at: Date.now(),
        kind: 'decline' as const,
      }),
    }),
    setWithdrawnResult: assign({
      counterResult: ({ context }: Scope) => ({
        responseId: context.responseId ?? context.negotiation?.jobResponse?.id,
        at: Date.now(),
        kind: 'withdraw' as const,
      }),
    }),
    setNegotiations: assign(({ event, context }: Scope) => {
      if (!isDoneGetEvent(event)) return {};

      const negotiations = getNegotiationsNewestFirst(event);
      // A brand-new MSaaS JobResponse returns an empty chain from the
      // by-response endpoint because the editor owns the creation of the
      // v1 row; the machine must not crash on empty output, and downstream
      // UI must handle the undefined case.
      const latestNegotiation = negotiations[0];

      return {
        negotiations,
        negotiation: latestNegotiation,
        latestVersion: latestNegotiation?.version ?? 0,
        // Empty chain: the viewer is always the party that can start a counter
        // (responder-side). Defaulting isReadOnly to false avoids the previous
        // crash on `output[0].respondingEmployerId` and matches the intent of
        // the first-negotiation path.
        isReadOnly: latestNegotiation
          ? isNegotiationReadOnlyForViewer(latestNegotiation, {
              employerUser: context.employerUser,
              provider: context.provider,
              viewerRole: context.viewerRole,
            })
          : false,
      };
    }),
    setLoading: assign({
      loading: true,
    }),
    resetLoading: assign({
      loading: false,
    }),
    setSubmitLifecycleBusy: assign({
      submitLifecycleBusy: true,
      loadFailed: false,
    }),
    resetSubmitLifecycleBusy: assign({
      submitLifecycleBusy: false,
      submitting: false,
      recoveryDeadlineAt: undefined,
    }),
    startRecoveryDeadline: assign({
      recoveryDeadlineAt: ({ context }) =>
        context.submitLifecycleBusy
          ? (context.recoveryDeadlineAt ?? Date.now() + MARKET_READ_TIMEOUT_MS)
          : undefined,
    }),
    setLoadFailed: assign({
      loadFailed: true,
    }),
    sendError: showAxiosErrorAction,
    // [MCV-1646] Submit-time 409/5xx alerts must hide backend internals and
    // read the same as the other listing actions (apply/message).
    sendSubmitError: sendListingActionError,
    sendReconciliationError: ({ context, system }) => {
      if (!context.submitError) return;
      sendCustomError({
        system: system as AppActorSystem,
        text: 'Unable to complete response',
        subText: 'Please try again. If the problem continues, contact support.',
      });
    },
  },
}).createMachine({
  context: () => ({
    negotiations: [],
    negotiation: undefined,
    employerUser: undefined,
    responseId: undefined,
    responseType: 'msaas',
    viewerRole: undefined,
    tabIndex: 0,
    loading: false,
    loadFailed: false,
    isReadOnly: false,
    offerModal: {
      open: false,
    },
    latestVersion: -1,
    provider: null,
    draft: undefined,
    draftSource: undefined,
    draftProviderIds: undefined,
    startDraftIfEmpty: false,
    submitting: false,
    submitLifecycleBusy: false,
    submitResult: undefined,
    submitResultSource: undefined,
    submitError: undefined,
    counterResult: undefined,
    acceptRefusal: undefined,
  }),
  initial: '$_VIEW',
  entry: assign({ offerModal: { open: false } }),
  on: {
    ON_SET_EMPLOYER_USER: [
      {
        guard: ({ context, event }) =>
          context.employerUser?.employerId !== event.payload.employerUser.employerId,
        target: '.$_VIEW',
        actions: [
          assign({ employerUser: ({ event }) => event.payload.employerUser }),
          'clearNegotiationScope',
          'resetLoading',
          'resetSubmitLifecycleBusy',
        ],
      },
      {
        actions: assign({
          employerUser: ({ event }) => event.payload.employerUser,
        }),
      },
    ],
    ON_SET_PROVIDER: [
      {
        guard: ({ context, event }) => context.provider?.id !== event.payload.provider.id,
        target: '.$_VIEW',
        actions: [
          assign({ provider: ({ event }) => event.payload.provider }),
          'clearNegotiationScope',
          'resetLoading',
          'resetSubmitLifecycleBusy',
        ],
      },
      {
        actions: assign({
          provider: ({ event }) => event.payload.provider,
        }),
      },
    ],
    ON_LOAD: [
      {
        // The actor is a singleton. Do not let another mounted entrypoint
        // cancel the submit invoke or retarget its authoritative reload.
        guard: ({ context }) => context.submitLifecycleBusy,
      },
      {
        guard: 'isReopeningUncertainSubmission',
        target: '.$_LOAD',
        actions: [assign({ loadFailed: false }), 'setLoading'],
      },
      {
        guard: ({ event }) =>
          event.payload?.responseId !== undefined && event.payload?.responseId !== null,
        target: '.$_LOAD',
        actions: [
          assign({
            employerUser: ({ event }) => event?.payload?.employerUser,
            responseId: ({ event }) => event?.payload?.responseId,
            responseType: ({ event }) => event?.payload?.responseType ?? 'msaas',
            viewerRole: ({ event }) => event?.payload?.viewerRole,
            startDraftIfEmpty: ({ event }) => event?.payload?.startDraftIfEmpty ?? false,
            // [MCV-1557] Response-SCOPED data, cleared the moment a different
            // response starts loading. This actor is a singleton, so without
            // this the previous response's chain survives into the new load:
            // if the new fetch then FAILS, `onError` only alerts and resets
            // loading, leaving the old negotiation in context. The detail view
            // would see loading=false plus a truthy negotiation, reset its form
            // from the PREVIOUS response and render it under the new one --
            // and Counter/Accept would submit the previous response's
            // jobResponse.id. Wrong data silently attributed to the wrong
            // response is worse than an empty panel.
            negotiations: () => [],
            negotiation: () => undefined,
            loadFailed: () => false,
            // [MCV-1646] A fresh ON_LOAD is a fresh scope; any draft/submit
            // state from a previous open must not leak into it.
            draft: () => undefined,
            draftSource: () => undefined,
            draftProviderIds: () => undefined,
            submitResult: () => undefined,
            submitResultSource: () => undefined,
            counterResult: () => undefined,
            acceptRefusal: () => undefined,
            submitError: () => undefined,
            submittedDraft: () => undefined,
          }),
          'setLoading',
        ],
      },
      {
        // No responseId: surface a clear error instead of silently loading
        // the wrong chain (previously hardcoded to listingId: '1').
        actions: [
          ({ system }) =>
            sendCustomError({
              system: system as AppActorSystem,
              text: 'Unable to load negotiation',
              subText:
                'A response id is required to open the B2B negotiation editor. Please re-open the opportunity from the opportunities page.',
            }),
        ],
      },
    ],
    // [MCV-1646] Open the editor directly on a draft, scoped to a bare
    // listing that has no JobResponse yet at all.
    ON_OPEN_DRAFT: [
      {
        guard: ({ context }) => context.submitLifecycleBusy,
      },
      {
        guard: (scope) => isReopeningUncertainSubmission(scope) && scope.context.responseId != null,
        target: '.$_LOAD',
        actions: [assign({ loadFailed: false }), 'setLoading'],
      },
      {
        guard: 'isReopeningUncertainSubmission',
        target: '.$_VIEW',
      },
      {
        target: '.$_LOAD_DRAFT',
        actions: [
          assign({
            employerUser: ({ event }) => (event as OnOpenDraftEvent).payload.employerUser,
            viewerRole: ({ event }) => (event as OnOpenDraftEvent).payload.viewerRole,
            responseType: ({ event }) =>
              (event as OnOpenDraftEvent).payload.listingType === 'JOB' ? 'job' : 'msaas',
            draftSource: ({ event }) => {
              const payload = (event as OnOpenDraftEvent).payload;
              return { listingType: payload.listingType, listingId: payload.listingId };
            },
            draftProviderIds: ({ event }) => (event as OnOpenDraftEvent).payload.providerIds,
            startDraftIfEmpty: false,
            // Response-scoped data does not exist yet for a bare-listing draft.
            responseId: () => undefined,
            negotiations: () => [],
            negotiation: () => undefined,
            loadFailed: () => false,
            draft: () => undefined,
            submitResult: () => undefined,
            submitResultSource: () => undefined,
            counterResult: () => undefined,
            acceptRefusal: () => undefined,
            submitError: () => undefined,
            submittedDraft: () => undefined,
          }),
          'setLoading',
        ],
      },
    ],
    // [MCV-1646] Guarded so a second Submit Offer click (or a stray resend)
    // while the first is still in flight is a silent no-op.
    ON_SUBMIT_DRAFT: {
      guard: ({ context }) =>
        context.draft != null && !context.submitLifecycleBusy && !context.submitError?.uncertain,
      target: '.$_SUBMIT_DRAFT',
      actions: [
        assign({
          submitting: true,
          submitError: undefined,
          submittedDraft: ({ context, event }) => ({
            data: structuredClone(event.data),
            providerIds: [...(event.providerIds ?? context.draftProviderIds ?? [])],
          }),
        }),
        'setSubmitLifecycleBusy',
      ],
    },
    ON_RETRY_SUBMIT_DRAFT: {
      guard: ({ context }) =>
        !!context.submittedDraft &&
        !!context.submitError?.uncertain &&
        !context.submitLifecycleBusy,
      target: '.$_SUBMIT_DRAFT',
      actions: [assign({ submitting: true, submitError: undefined }), 'setSubmitLifecycleBusy'],
    },
    ON_CONSUME_SUBMIT_RESULT: {
      // A mounted consumer can be finishing an older effect while a newer
      // submit has already completed. Only consume the exact result named by
      // the event so the older consumer cannot erase the newer notification.
      guard: ({ context, event }) =>
        context.submitResult?.negotiationId === event.payload.negotiationId,
      actions: assign({ submitResult: undefined, submitResultSource: undefined }),
    },
    ON_CONSUME_COUNTER_RESULT: {
      // Same exact-identity rule as ON_CONSUME_SUBMIT_RESULT: a stale consumer
      // must not erase a newer counter's notification.
      guard: ({ context, event }) => context.counterResult?.at === event.payload.at,
      actions: assign({ counterResult: undefined }),
    },
    ON_CONSUME_ACCEPT_REFUSAL: {
      guard: ({ context, event }) => context.acceptRefusal?.at === event.payload.at,
      actions: assign({ acceptRefusal: undefined }),
    },
    ON_SET_TAB_INDEX: {
      actions: assign({
        tabIndex: ({ event }) => event?.payload?.index,
      }),
    },
    ON_CALCULATE: {
      target: '.$_CALCULATE',
    },
    ON_REVIEW_OFFER: {
      actions: assign({
        negotiation: ({ context, event }) => {
          const negotiationId = event.id;
          return context.negotiations?.find((n) => n.id === negotiationId);
        },
      }),
      target: '.$_OFFER',
    },
  },
  states: {
    $_VIEW: {
      entry: assign({
        offerModal: { open: false },
      }),
      on: {
        // The current employer route renders negotiation details directly instead of
        // first entering the legacy review modal state.
        ON_ACCEPT_OFFER: {
          target: '$_ACCEPT',
        },
        ON_REJECT_OFFER: {
          target: '$_REJECT',
        },
        ON_WITHDRAW_OFFER: {
          target: '$_WITHDRAW',
        },
        ON_COUNTER_OFFER: {
          target: '$_COUNTER',
        },
        ON_CLOSE_OFFER: {
          actions: [assign({ offerModal: { open: false } })],
          target: '$_VIEW',
        },
      },
    },
    $_LOAD: {
      entry: 'startRecoveryDeadline',
      invoke: {
        src: 'getNegotiations',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: [
          {
            // [MCV-1646] Empty chain + the caller asked for a draft-first open
            // + no draft already in hand (a reconciled network-error retry
            // keeps its existing draft object rather than re-fetching -- see
            // $_SUBMIT_DRAFT's onError). Stay "loading" straight into
            // $_LOAD_DRAFT rather than flashing the empty state first.
            guard: ({ context, event }) =>
              (event as DoneGetEvent).output.length === 0 &&
              context.startDraftIfEmpty &&
              !context.draft,
            target: '$_LOAD_DRAFT',
            actions: [
              'setNegotiations',
              assign({
                draftSource: ({ context }) =>
                  context.responseId !== undefined
                    ? { responseId: context.responseId }
                    : context.draftSource,
              }),
            ],
          },
          {
            guard: ({ event }) => (event as DoneGetEvent).output.length > 0,
            target: '$_VIEW',
            actions: [
              'setNegotiations',
              'resetLoading',
              'resetSubmitLifecycleBusy',
              assign({
                draft: undefined,
                submitError: undefined,
                submittedDraft: undefined,
                startDraftIfEmpty: false,
              }),
            ],
          },
          {
            // A submit with an indeterminate network/5xx outcome is reconciled
            // by reloading its known response. If the chain is still empty,
            // the original request may still commit: retain the draft and
            // original submission without swallowing the uncertainty.
            guard: ({ context, event }) =>
              (event as DoneGetEvent).output.length === 0 && context.submitError != null,
            target: '$_VIEW',
            actions: [
              'setNegotiations',
              'resetLoading',
              'resetSubmitLifecycleBusy',
              'sendReconciliationError',
              assign({ startDraftIfEmpty: false }),
            ],
          },
          {
            target: '$_VIEW',
            actions: ['setNegotiations', 'resetLoading', 'resetSubmitLifecycleBusy'],
          },
        ],
        onError: {
          target: '$_VIEW',
          // [MCV-1557] `loadFailed` distinguishes "this response genuinely has
          // no negotiation yet" from "we could not find out". Both leave
          // negotiation undefined, but only the first should tell the user
          // there is nothing here. [MCV-1646] Never fall into the draft path
          // on a genuine load failure -- an error must stay an error.
          actions: ['sendError', 'resetLoading', 'resetSubmitLifecycleBusy', 'setLoadFailed'],
        },
      },
    },
    $_LOAD_DRAFT: {
      entry: 'startRecoveryDeadline',
      invoke: {
        src: 'getDraft',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_VIEW',
          actions: [
            assign({
              draft: ({ context, event }) =>
                transformDraftApiToDraft(
                  (event as DoneActorEvent<NegotiationDraftApiResponse>).output,
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  context.draftSource!,
                  context.draftProviderIds
                ),
              responseId: ({ context, event }) =>
                (event as DoneActorEvent<NegotiationDraftApiResponse>).output.jobResponseId ??
                context.responseId,
            }),
            'resetLoading',
            'resetSubmitLifecycleBusy',
          ],
        },
        onError: [
          {
            // [MCV-1646] Someone else (or a same-caller retry that lost a
            // race) already started this chain between the by-response load
            // and this draft fetch. Adopt the responseId the server names and
            // fall through to $_LOAD so the real chain and turn are shown
            // instead of a stale draft.
            guard: ({ event }) =>
              isNegotiationAlreadyStartedError((event as ErrorActorEvent).error),
            target: '$_LOAD',
            actions: assign({
              responseId: ({ context, event }) =>
                getAlreadyStartedResponseId((event as ErrorActorEvent).error) ?? context.responseId,
              startDraftIfEmpty: false,
            }),
          },
          {
            target: '$_VIEW',
            actions: ['sendError', 'setLoadFailed', 'resetLoading', 'resetSubmitLifecycleBusy'],
          },
        ],
      },
    },
    $_SUBMIT_DRAFT: {
      on: {
        // [MCV-1646] Mirror the listing-search $_START_NEGOTIATE pattern:
        // intercept close so the root-level handler does not exit this state
        // while the submit POST is in flight -- XState 5 discards a pending
        // onDone once its invoking state exits.
        ON_CLOSE_OFFER: {
          // Intentionally consumed without closing. Exiting this invoking
          // state would discard the pending submit result.
        },
      },
      invoke: {
        src: 'submitDraft',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_LOAD',
          actions: [
            assign({
              submitResult: ({ event }) => (event as DoneActorEvent<DraftSubmitResult>).output,
              submitResultSource: ({ context }) => context.draftSource,
              responseId: ({ event }) =>
                (event as DoneActorEvent<DraftSubmitResult>).output.jobResponseId,
              draft: undefined,
              submittedDraft: undefined,
              submitting: false,
              startDraftIfEmpty: false,
            }),
            'setLoading',
          ],
        },
        onError: [
          {
            // 400: invalid terms (including the MSaaS allocation rule). Keep
            // the draft untouched so the user's edits survive the round trip.
            guard: ({ event }) => getErrorStatus((event as ErrorActorEvent).error) === 400,
            target: '$_VIEW',
            actions: [assign({ submitting: false }), 'resetSubmitLifecycleBusy', 'sendError'],
          },
          {
            // 409 with a known responseId: the other party won the race.
            // Clear the draft and reload the real chain, which now derives
            // the correct turn.
            guard: ({ context, event }) =>
              isNegotiationAlreadyStartedError((event as ErrorActorEvent).error) &&
              (context.responseId !== undefined ||
                getAlreadyStartedResponseId((event as ErrorActorEvent).error) !== undefined),
            target: '$_LOAD',
            actions: [
              assign({
                submitting: false,
                draft: undefined,
                responseId: ({ context, event }) =>
                  getAlreadyStartedResponseId((event as ErrorActorEvent).error) ??
                  context.responseId,
                startDraftIfEmpty: false,
              }),
              'setLoading',
            ],
          },
          {
            // 409 without a known responseId: cannot safely reload a chain we
            // cannot identify. Surface the error and keep the draft.
            guard: ({ event }) =>
              isNegotiationAlreadyStartedError((event as ErrorActorEvent).error),
            target: '$_VIEW',
            actions: [assign({ submitting: false }), 'resetSubmitLifecycleBusy', 'sendSubmitError'],
          },
          {
            // Network/5xx with a known responseId: reconcile against the real
            // chain rather than assuming the submit failed outright -- it may
            // have landed. An empty chain can also precede a pending commit,
            // so retain the draft and uncertain outcome. A non-empty chain
            // provides canonical state and $_LOAD's onDone clears the draft.
            guard: ({ context }) => context.responseId !== undefined,
            target: '$_LOAD',
            actions: [
              assign({
                submitting: false,
                submitError: ({ event }) => ({
                  status: getErrorStatus((event as ErrorActorEvent).error),
                  message: getErrorMessage(event as ErrorActorEvent),
                  uncertain: isUncertainListingActionError((event as ErrorActorEvent).error),
                }),
                startDraftIfEmpty: true,
              }),
              'setLoading',
            ],
          },
          {
            target: '$_VIEW',
            actions: [
              assign({
                submitting: false,
                submitError: ({ event }) => ({
                  status: getErrorStatus((event as ErrorActorEvent).error),
                  message: getErrorMessage(event as ErrorActorEvent),
                  uncertain: isUncertainListingActionError((event as ErrorActorEvent).error),
                }),
              }),
              'resetSubmitLifecycleBusy',
              'sendSubmitError',
            ],
          },
        ],
      },
    },
    $_CALCULATE: {
      invoke: {
        src: 'calculate',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_VIEW',
          actions: ['resetLoading'],
        },
        onError: {
          target: '$_VIEW',
          actions: ['sendError', 'resetLoading'],
        },
      },
    },
    $_OFFER: {
      entry: [
        assign({ offerModal: { open: true } }),
        () => console.log('Entered $_OFFER state - offerModal opened'),
      ],
      // review offer terms
      on: {
        ON_ACCEPT_OFFER: {
          target: '$_ACCEPT',
        },
        ON_REJECT_OFFER: {
          target: '$_REJECT',
        },
        ON_WITHDRAW_OFFER: {
          target: '$_WITHDRAW',
        },
        ON_COUNTER_OFFER: {
          target: '$_COUNTER',
        },
        ON_CLOSE_OFFER: {
          actions: [assign({ offerModal: { open: false } })],
          target: '$_VIEW',
        },
      },
    },
    // [MCV-1850] A decline or withdrawal ends the exchange and notifies the other
    // party, so -- like a counter -- it hands the details view a result that
    // closes the popup.
    $_REJECT: {
      invoke: {
        src: 'rejectNegotiation',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_VIEW',
          actions: ['resetLoading', 'setDeclinedResult'],
        },
        onError: {
          target: '$_VIEW',
          actions: ['sendError', 'resetLoading'],
        },
      },
    },
    $_WITHDRAW: {
      invoke: {
        src: 'withdrawNegotiation',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_VIEW',
          actions: ['resetLoading', 'setWithdrawnResult'],
        },
        onError: {
          target: '$_VIEW',
          actions: ['sendError', 'resetLoading'],
        },
      },
    },
    $_ACCEPT: {
      invoke: {
        src: 'acceptNegotiation',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: [
          {
            // [MCV-1954] The accept may have completed the agreement (status ACCEPTED, contract
            // created); reload the chain so the details view can send the viewer to Contracts.
            guard: ({ context }) => context.responseId !== undefined,
            target: '$_LOAD',
            actions: ['setLoading'],
          },
          {
            target: '$_VIEW',
            actions: ['resetLoading'],
          },
        ],
        onError: [
          {
            // [MCV-1858] A refused fee schedule is not an error to alert on: the details view
            // reopens the fee dialog with the reason.
            guard: ({ event }) => feeRefusalCode((event as ErrorActorEvent).error) !== null,
            target: '$_VIEW',
            actions: [
              assign({
                acceptRefusal: ({ context, event }) => ({
                  responseId: context.responseId ?? context.negotiation?.jobResponse?.id,
                  at: Date.now(),
                  code: feeRefusalCode((event as ErrorActorEvent).error) as FeeRefusalCode,
                }),
              }),
              'resetLoading',
            ],
          },
          {
            // [MCV-1858] The listing owner (payer) has not authorized the current fee schedule, so
            // this responder cannot accept yet. They are not the payer, so the fee dialog never
            // opens: tell them why and reload the chain, since the server cleared the payer's
            // acceptance.
            guard: ({ context, event }) =>
              isPayerFeeAuthorizationRequired((event as ErrorActorEvent).error) &&
              context.responseId !== undefined,
            target: '$_LOAD',
            actions: ['sendSubmitError', 'setLoading'],
          },
          {
            guard: ({ event }) => isPayerFeeAuthorizationRequired((event as ErrorActorEvent).error),
            target: '$_VIEW',
            actions: ['sendSubmitError', 'resetLoading'],
          },
          {
            // [MCV-1889] The accept lost a race (the listing filled, or a concurrent write held the
            // same rows). Say so and reload the chain, which is now out of date.
            guard: ({ context, event }) =>
              acceptConflictCode((event as ErrorActorEvent).error) !== null &&
              context.responseId !== undefined,
            target: '$_LOAD',
            actions: ['sendSubmitError', 'setLoading'],
          },
          {
            target: '$_VIEW',
            actions: ['sendError', 'resetLoading'],
          },
        ],
      },
    },
    $_COUNTER: {
      invoke: {
        src: 'counterNegotiation',
        input: ({ context, event }: Scope) => ({ context, event }),
        onDone: {
          target: '$_LOAD',
          // [MCV-1816] The counter went to the other party, so the details view
          // closes the popup. It reads this result, then consumes it.
          actions: [
            'resetLoading',
            assign({
              counterResult: ({ context }) => ({
                responseId: context.responseId ?? context.negotiation?.jobResponse?.id,
                at: Date.now(),
              }),
            }),
          ],
        },
        onError: {
          target: '$_VIEW',
          actions: ['sendError', 'resetLoading'],
        },
      },
    },
  },
});
